import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

// Generate a UUID v4
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Define types for Consent Manager and Clarity
declare global {
  interface Window {
    cmp?: {
      (command: 'OnConsentReceived', callback: (consent: ConsentData) => void): void;
      (command: 'getConsentData', callback: (consent: ConsentData, isV2: boolean) => void): void;
      (command: 'openCmpConsentSettings'): void;
    };

    // Allow for the specific addEventListener signature found in docs: __cmp('cmd', [args], null)
    __cmp?: (command: string, parameter?: unknown, callback?: unknown) => void;
    __tcfapi?: (
      command: 'addEventListener' | 'removeEventListener' | 'getTCData' | 'ping',
      version: 2,
      callback: (tcData: TCData, success: boolean) => void,
      parameter?: unknown
    ) => void;

    // Microsoft Clarity (loaded via script tag with ConsentManager)
    clarity?: (command: string, ...args: unknown[]) => void;
    // Reddit Pixel (loaded via script tag with ConsentManager)
    rdt?: (command: string, ...args: unknown[]) => void;
    // Meta Pixel (loaded via script tag with ConsentManager)
    fbq?: (command: string, ...args: unknown[]) => void;
    // X (Twitter) Pixel (loaded via script tag with ConsentManager)
    twq?: (command: string, ...args: unknown[]) => void;
  }
}

interface TCData {
  tcString: string;
  tcfPolicyVersion: number;
  cmpId: number;
  cmpVersion: number;
  gdprApplies: boolean;
  eventStatus: 'tcloaded' | 'cmpuishown' | 'useractioncomplete';
  cmpStatus: 'stub' | 'loading' | 'loaded' | 'error';
  listenerId: number | undefined;
  objectCategoryId: number | undefined;
  purpose: {
    consents: { [key: number]: boolean };
    legitimateInterests: { [key: number]: boolean };
  };
  vendor: {
    consents: { [key: number]: boolean };
    legitimateInterests: { [key: number]: boolean };
  };
}

interface ConsentData {
  consent: boolean;
  // We can add more specific vendor/purpose checks here if needed
  vendors?: Record<string, boolean>;
  purposes?: Record<string, boolean>;
}

// Storage keys
const STORAGE_KEYS = {
  CLARITY_ID: 'claroclaim_clarity_id',
};

class MonitoringService {
  private appInsights: ApplicationInsights | null = null;
  private reactPlugin: ReactPlugin | null = null;
  private isInitialized = false;
  private consentGiven = false;
  private listeners: ((consent: boolean) => void)[] = [];

  constructor() {
    // Initial state is no consent until CMP tells us otherwise
    this.consentGiven = false;
  }

  /**
   * Gets or creates a stable Clarity ID for session linking
   * This ID persists in localStorage and is used to identify the user in Clarity
   */
  public getClarityId(): string {
    if (typeof localStorage === 'undefined') return '';

    let clarityId = localStorage.getItem(STORAGE_KEYS.CLARITY_ID);
    if (!clarityId) {
      clarityId = generateUUID();
      localStorage.setItem(STORAGE_KEYS.CLARITY_ID, clarityId);
    }
    return clarityId;
  }
  
  public subscribe(callback: (consent: boolean) => void): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.consentGiven));
  }

  public initialize(connectionString?: string): void {
    if (typeof window === 'undefined') return;

    // Set up the connection string for later use
    const appInsightsConnectionString = connectionString || import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING;

    // Check for standard TCF API (v2)
    // This is preferred over 'cmp' or '__cmp' which are proprietary/legacy
    if (window.__tcfapi) {
      
      try {
        window.__tcfapi('addEventListener', 2, (tcData, success) => {
          if (success && tcData && (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete')) {
             // Check for Purpose 1 (Storage/Access) + Purpose 7/8/9/10 (Measurement/Analytics)
             // If GDPR doesn't apply, we assume consent is given (or not required).
             // Otherwise, check for Purpose 1 (Storage) which is required for analytics cookies.
             const hasStorageConsent = tcData.gdprApplies === false || tcData.purpose.consents[1] === true;
             
             // Create a synthetic "ConsentData" object for our internal handler
             const mappedConsent: ConsentData = {
               consent: hasStorageConsent,
               purposes: Object.keys(tcData.purpose.consents || {}).reduce((acc, key) => {
                 acc[key] = tcData.purpose.consents[Number(key)];
                 return acc;
               }, {} as Record<string, boolean>)
             };
             
             this.handleConsentUpdate(mappedConsent, appInsightsConnectionString);
          }
        });
      } catch (e) {
        console.error('MonitoringService: Failed to register TCF listener', e);
      }

      // Check consent immediately
      this.checkConsent(appInsightsConnectionString);

      // Re-check on user interactions (click, keyup, focus) to catch CMP updates
      // This avoids polling but catches the "Accept" action reasonably fast
      const checkWithDelay = () => setTimeout(() => this.checkConsent(appInsightsConnectionString), 500);

      window.addEventListener('click', checkWithDelay);
      window.addEventListener('keyup', checkWithDelay);
      window.addEventListener('focus', () => this.checkConsent(appInsightsConnectionString));
      
      // Official Consent Manager Event Listener
      // Check for proprietary __cmp event as per docs: __cmp("addEventListener", ["eventname", callableFunction, capture], null)
      if (window.__cmp) {
        try {
           const consentCallback = (_data: unknown) => {
              this.checkConsent(appInsightsConnectionString);
           };

           // We try multiple events to be safe: 'consent', 'consentapproved', 'consentcustom'
           const events = ['consent', 'consentapproved', 'consentcustom'];
           
           events.forEach(evt => {
               (window.__cmp as unknown as (cmd: string, args: unknown[], cb: null) => void)('addEventListener', [evt, consentCallback, false], null);
           });
           
        } catch (e) {
           console.error('MonitoringService: Failed to register __cmp listener', e);
        }
      }

    } else {
      // Fallback to legacy/polling logic if TCF not present (unlikely with this stub)
      const cmpApi = window.cmp || window.__cmp;

      if (cmpApi) {
        // Try the array signature here too just in case
        try {
             const consentCallback = (data: ConsentData) => {
                this.handleConsentUpdate(data, appInsightsConnectionString);
             };
             (cmpApi as unknown as (cmd: string, args: unknown[], cb: null) => void)('addEventListener', ['consent', consentCallback, false], null);
        } catch {
            // If that fails, try the old way as last resort
             (cmpApi as unknown as (cmd: string, cb: (data: ConsentData) => void) => void)('OnConsentReceived', (data: ConsentData) => {
                this.handleConsentUpdate(data, appInsightsConnectionString);
             });
        }
      }
    }
  }

  private checkConsent(connectionString?: string): void {
     if (!window.__tcfapi) return;

     try {
       window.__tcfapi('getTCData', 2, (tcData, success) => {
          if (success && tcData) {
             const hasStorageConsent = tcData.gdprApplies === false || tcData.purpose.consents[1] === true;
             
             // Only update if changed or first run (implicit)
             if (hasStorageConsent !== this.consentGiven) {
                 const mappedConsent: ConsentData = {
                   consent: hasStorageConsent,
                   purposes: Object.keys(tcData.purpose.consents || {}).reduce((acc, key) => {
                     acc[key] = tcData.purpose.consents[Number(key)];
                     return acc;
                   }, {} as Record<string, boolean>)
                 };
                 this.handleConsentUpdate(mappedConsent, connectionString);
             }
          }
       });
     } catch {
        // ignore errors
     }
  }

  private handleConsentUpdate(data: ConsentData, connectionString?: string): void {

    // Logic to determine if we have consent for analytics/marketing
    // For now, we use the general 'consent' flag or specific purposes if we knew IDs.
    // Assuming data.consent is true if the user accepted necessary marketing/analytics cookies.
    const hasConsent = data.consent === true;

    if (hasConsent) {
      this.grantConsent(connectionString);
    } else {
      this.revokeConsent();
    }
  }

  private grantConsent(connectionString?: string): void {

    if (this.consentGiven && this.isInitialized) return;

    this.consentGiven = true;
    
    // Initialize React Plugin for Application Insights
    if (!this.reactPlugin) {
      this.reactPlugin = new ReactPlugin();
    }

    // Initialize Application Insights if not already done
    if (connectionString && !this.appInsights) {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,
          extensions: [this.reactPlugin],
          extensionConfig: {
            [this.reactPlugin.identifier]: {},
          },
          disableCookiesUsage: false,
          disableAjaxTracking: false,
          disableFetchTracking: false,
          enableAutoRouteTracking: true,
          enableCorsCorrelation: true,
          correlationHeaderExcludedDomains: [
            '*.wistia.com',
            '*.wistia.net',
            'fast.wistia.com',
            'fast.wistia.net',
            'embedwistia-a.akamaihd.net'
          ],
          enableRequestHeaderTracking: false,
          enableResponseHeaderTracking: false,
          disableExceptionTracking: false,
          disableTelemetry: false,
          maxBatchInterval: 15000,
          disableDataLossAnalysis: false,
          isStorageUseDisabled: false,
          isCookieUseDisabled: false,
        },
      });

      this.appInsights.loadAppInsights();
      
      // Sanitizer
      this.appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.tags) {
          delete envelope.tags['ai.user.accountId'];
          delete envelope.tags['ai.user.id'];
          delete envelope.tags['ai.user.authUserId'];
        }
        return true;
      });
    } else if (this.appInsights) {
        // Re-enable if previously disabled
        this.appInsights.config.disableCookiesUsage = false;
        this.appInsights.config.disableTelemetry = false;
        this.appInsights.config.isStorageUseDisabled = false;
        this.appInsights.config.isCookieUseDisabled = false;
    }

    // Clarity is now loaded via script tag in index.html with ConsentManager blocking
    // Set up stable identity for session linking if Clarity is available
    if (window.clarity) {
      const clarityId = this.getClarityId();
      if (clarityId) {
        window.clarity('identify', clarityId);
      }
    }

    this.isInitialized = true;
    this.notifyListeners();
  }

  private revokeConsent(): void {
    this.consentGiven = false;

    // Disable App Insights
    if (this.appInsights) {
      this.appInsights.config.disableCookiesUsage = true;
      this.appInsights.config.disableTelemetry = true;
      this.appInsights.config.isStorageUseDisabled = true;
      this.appInsights.config.isCookieUseDisabled = true;
    }
    
    // For Clarity, we can't fully "un-init", but we can tell it consent is revoked if it supports it via API
    // Try to clear cookies manually to stop tracking from persisting.
    this.clearAnalyticsCookies();
    this.notifyListeners();
  }

  private clearAnalyticsCookies(): void {
    if (typeof document === 'undefined') return;

    const cookiesToClear = [
      'ai_user',
      'ai_session',
      '_clck',
      '_clsk',
      '_clarity',
    ];

    cookiesToClear.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
    });
  }

  public trackEvent(name: string, properties?: Record<string, unknown>): void {
    if (!this.consentGiven || !this.appInsights) return;
    const sanitizedProperties = this.sanitizeProperties(properties);
    this.appInsights.trackEvent({ name }, sanitizedProperties);
  }

  public trackException(exception: Error, properties?: Record<string, unknown>): void {
    if (!this.consentGiven || !this.appInsights) return;
    const sanitizedProperties = this.sanitizeProperties(properties);
    this.appInsights.trackException({
      exception,
      properties: sanitizedProperties
    });
  }

  public trackPageView(name?: string, properties?: Record<string, unknown>): void {
    if (!this.consentGiven) return;

    // Track in Application Insights if available
    if (this.appInsights) {
      const sanitizedProperties = this.sanitizeProperties(properties);
      this.appInsights.trackPageView({
        name,
        properties: sanitizedProperties
      });
    }

    // Re-identify on each page view for optimal Clarity tracking
    const clarityId = this.getClarityId();
    if (clarityId && window.clarity) {
      try {
        window.clarity('identify', clarityId);
      } catch {
        // Clarity may not be loaded yet
      }
    }
  }

  public trackClarityEvent(name: string): void {
    // Clarity is loaded via ConsentManager - if window.clarity exists, consent was given
    if (!window.clarity) return;
    try {
      window.clarity('event', name);
    } catch (e) {
      console.error('Failed to track Clarity event:', e);
    }
  }

  public trackRedditEvent(eventName: string, conversionId?: string): void {
    // Reddit Pixel is loaded via ConsentManager - if window.rdt exists, consent was given
    if (!window.rdt) return;
    try {
      // Always include a conversion ID for deduplication
      const id = conversionId || generateUUID();
      window.rdt('track', eventName, { conversionId: id });
    } catch (e) {
      console.error('Failed to track Reddit event:', e);
    }
  }

  public identifyRedditUser(email: string): void {
    // Re-init Reddit Pixel with advanced matching (email)
    // Reddit automatically hashes the email
    if (!window.rdt) return;
    try {
      window.rdt('init', 'a2_i8go08zmgpth', {
        email: email,
      });
    } catch (e) {
      console.error('Failed to identify Reddit user:', e);
    }
  }

  public trackMetaEvent(eventName: string, eventId?: string): void {
    // Meta Pixel is loaded via ConsentManager - if window.fbq exists, consent was given
    if (!window.fbq) return;
    try {
      // Include eventID for deduplication
      const id = eventId || generateUUID();
      window.fbq('track', eventName, {}, { eventID: id });
    } catch (e) {
      console.error('Failed to track Meta event:', e);
    }
  }

  public identifyMetaUser(email: string): void {
    // Set user data for Meta advanced matching
    // Meta automatically hashes the data
    if (!window.fbq) return;
    try {
      window.fbq('init', '1038498498001498', {
        em: email,
      });
    } catch (e) {
      console.error('Failed to identify Meta user:', e);
    }
  }

  public trackXEvent(eventName: string, conversionId?: string): void {
    // X (Twitter) Pixel is loaded via ConsentManager - if window.twq exists, consent was given
    if (!window.twq) return;
    try {
      // Include conversion ID for deduplication
      const id = conversionId || generateUUID();
      window.twq('event', eventName, { conversion_id: id });
    } catch (e) {
      console.error('Failed to track X event:', e);
    }
  }

  public identifyXUser(email: string): void {
    // Set user data for X advanced matching
    // X automatically hashes the data
    if (!window.twq) return;
    try {
      window.twq('config', 'qy0lg', {
        em: email,
      });
    } catch (e) {
      console.error('Failed to identify X user:', e);
    }
  }

  private sanitizeProperties(properties?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!properties) return undefined;
    const sanitized = { ...properties };
    const piiFields = ['email', 'name', 'phone', 'address', 'ssn', 'password'];
    piiFields.forEach(field => {
      delete sanitized[field];
    });
    return sanitized;
  }

  public getReactPlugin(): ReactPlugin | null {
    return this.reactPlugin;
  }

  public isConsentGiven(): boolean {
    return this.consentGiven;
  }
  
  public openConsentSettings(): void {
    if (window.cmp) {
        window.cmp('openCmpConsentSettings');
    }
  }
}

export const monitoringService = new MonitoringService();